/**
 * Single source for the public claims that appear on more than one surface.
 *
 * DYN-158. The integration answer was duplicated in Pricing.tsx and
 * FAQSection.tsx and the two copies drifted apart: one said "50+ integrations",
 * the other "40+". Nobody noticed, because nothing compared them. Duplicated
 * marketing copy does not stay in sync by good intentions, so the two surfaces
 * now import the same string and a divergence is a compile error rather than a
 * contradiction a customer finds.
 *
 * EVERY SENTENCE HERE MUST BE CHECKABLE AGAINST THE BACKEND. The evidence is
 * cited inline so the next person can re-check it rather than trust it. If you
 * change what the product does, change this file in the same commit.
 */

/**
 * Integration status.
 *
 * Verified 2026-09-03 against:
 *   integrations.py:486  OAUTH_PROVIDERS = ("stripe", "clover", "quickbooks",
 *                        "google_analytics", "hubspot")
 *   clover_sync.py, clover_dispatcher.py, clover_webhook.py, clover_reconcile.py
 *   stripe_connect_service.py
 *   quickbooks_service.py:228   sync_data, real httpx calls
 *   google_analytics_service.py
 *   hubspot_service.py:329      "TODO: implement real HubSpot data sync"
 *   shopify_service.py:270      sync_placeholder
 *   toast_service.py:274,294,338,372  "Not implemented" / "STUB IMPLEMENTATION"
 *   Square: no OAuth path anywhere. It appears only as a catalogue entry.
 *   WooCommerce, Xero, Salesforce: no model, no service, no OAuth. Nothing.
 *
 * Deliberately NOT a count. "5 integrations" would be a second invented number
 * and it would flatten four genuinely different states into one figure. A buyer
 * asking "is my POS supported?" cannot use a count; they can use this list.
 *
 * Deliberately NOT claiming these work end to end for a given merchant. What is
 * asserted is which code paths exist, which is what was actually checked.
 */
export const INTEGRATION_STATUS =
  'Clover and Stripe connect over OAuth and are the only two with implemented sync paths. ' +
  'Google Analytics connects, but its sync has no route, so nothing can call it and it moves no data. ' +
  'QuickBooks connects over OAuth only. Its sync is not implemented, so connecting it moves no data. ' +
  'HubSpot connects, but its data sync is not written yet. ' +
  'Toast and Shopify have incomplete code and no usable connection. ' +
  'Square is only listed in the integration catalogue; no connection is implemented. ' +
  'WooCommerce, Xero and Salesforce are not supported in any form. ' +
  'Tell us what you run and we will tell you where it stands.';

/**
 * Security and compliance.
 *
 * Verified 2026-09-03 against:
 *   grep for soc2 / SOC 2 / audit artefacts across the repo: nothing.
 *   models.py:772 and the other *Connection models use EncryptedString /
 *     QuickBooksEncryptedString / ShopifyEncryptedString / HubSpotEncryptedString,
 *     so tokens are encrypted by the application before they are stored.
 *   api/endpoints/export.py and services/account_deletion_service.py exist, and
 *     Settings.tsx:316 routes "data" to settings/DataSettings, which renders
 *     both the export dialog and the account-deletion flow. So the UI path is
 *     real, not just the endpoint.
 *   Privacy.tsx:257 "We do not sell personal information to third parties."
 *
 * On wording: HIPAA and GDPR have no certification regime to hold, so this says
 * the work has not been done rather than that we lack a certificate. Saying
 * "not certified" against a standard nobody certifies is its own small
 * inaccuracy, and it invites the reader to assume a certificate is the only
 * thing missing.
 */
export const SECURITY_POSTURE =
  'We have not completed a SOC 2 audit and we do not claim SOC 2 certification. ' +
  'We have also not done the work needed to represent this service as GDPR compliant, so we do not claim it. ' +
  'What is true and checkable: the site is served over HTTPS, and the OAuth tokens for your connected ' +
  'accounts are encrypted by the application before they reach the database. ' +
  'You can export your data and delete your account yourself from Settings, under Data. ' +
  'Our privacy policy states that we do not sell personal information to third parties.';

/**
 * The ribbon on the middle pricing tier.
 *
 * DYN-266. It read "Most Popular" on both the pricing page and the landing
 * page preview. That is a claim about what other customers chose, and there are
 * no other customers, so it was false on the day it was written and would only
 * become true by accident.
 *
 * The ribbon itself is not the problem and is not being deleted: it does real
 * navigational work, and a four tier table is harder to read without a
 * suggested starting point. What changes is whose opinion it reports. "Our
 * recommendation" is a claim about us, which means it is true when written and
 * stays true regardless of who buys what.
 *
 * "Includes a setup call" was considered and rejected: it is factual, but the
 * Managed tier includes more human contact than Guided does, so a feature
 * callout on the middle tier invites a comparison it loses. An opinion does not
 * have that problem.
 *
 * One string, imported by both surfaces, for the same reason as everything else
 * in this file. Ruling by the marketing owner, 2026-09-10.
 */
export const RECOMMENDED_TIER_RIBBON = 'Our recommendation';

/**
 * What the word "transaction" means on the meter.
 *
 * DYN-245 and DYN-252. Chase chose option A on 2026-09-13: change the wording
 * to match what the code already counts, rather than rebuild the count. The
 * wording below is the business session's draft, reproduced as written, and it
 * is not mine to improve.
 *
 * THERE WERE TWO COPIES AND THE ONE NEARER THE CUSTOMER WAS LOOSER, which is
 * the whole reason this constant exists. Pricing.tsx named Clover and Stripe
 * and listed "orders, refunds and partial refunds". FAQSection.tsx, which is on
 * the live homepage and is therefore read by more people, said "any sales
 * record imported from your connected platforms (orders, refunds, etc.)" and
 * tied the historical carve-out to onboarding. Every difference ran the same
 * direction: an open "etc.", platforms the site elsewhere lists but cannot
 * connect, and a carve-out scoped to a moment in the customer's lifecycle when
 * the real rule is the sale date and applies forever. GL-09 with a customer
 * facing edge.
 *
 * Verified 2026-09-08 by the business session against the code that produces
 * the number, re-checked here before the wording shipped:
 *   billing_service.py:550-577  one row per Clover order, counted on
 *                               business_date, the merchant's own trading day,
 *                               not ordered_at. A bar's night runs past
 *                               midnight, so an order rung at 1am belongs to
 *                               the previous trading day.
 *   models_phase1.py:345-455    PosOrder has is_voided as a boolean and no
 *                               refund row and no refund column anywhere, so a
 *                               refunded order cannot count twice. Voided
 *                               orders ARE included in the count, which is why
 *                               the copy says a void does not add one rather
 *                               than saying a void is excluded.
 *   Stripe has no per-charge row; the count is summed from daily
 *   NormalizedMetric rows, so it cannot be stated per charge.
 *   Manual CSV import is excluded outright, whatever its dates.
 *
 * The carve-out is deliberately not what either live version promised. Both
 * said bulk historical imports do not count and the homepage tied that to
 * onboarding. The rule is the sale date: an order from three months ago never
 * counts against this month however recently it arrived, and an order from this
 * month does count even if it arrived in a backfill. A customer who connects
 * Clover mid-month and watches this month's sales land on their meter was
 * previously promised otherwise.
 *
 * One paragraph rather than three because both surfaces render this inside a
 * single MUI Typography, where newlines collapse.
 */
export const TRANSACTION_DEFINITION =
  'A transaction is one sales record imported from a connected account, which today means Clover and Stripe. ' +
  'Shopify and Square are not connected and cannot import anything yet. ' +
  'A Clover order counts once, on your own trading day rather than on the day we imported it, ' +
  'so a sale rung at 1am counts to the night before. ' +
  'A refund or a void does not add a second one; the order it belongs to still counts once. ' +
  'Stripe does not give us individual charges, so Stripe is counted from its daily totals. ' +
  'Two things never count against your monthly limit: sales from before the current billing period, ' +
  'however recently you imported them, and anything you upload yourself as a CSV.';
